import { useState } from "react";
import { ArrowRight, Check, Mail } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

export default function Contact() {
  useSeo({
    title: "Contact — Pip Bennett",
    description: "Get in touch with Pip Bennett for media, partnerships, workplace workshops, or general enquiries.",
    canonical: "https://pipbennett.com/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Unable to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-32" data-testid="page-contact">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <SectionReveal>
          <div className="eyebrow mb-4 text-sage">Get in touch</div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]">
            Say <em className="italic text-sage">hello</em>.
          </h1>
          <p className="mt-6 text-ink-soft max-w-xl leading-relaxed text-lg">
            Questions about the program, a workplace workshop, a podcast collaboration, or just a note — Pip reads
            every message personally.
          </p>
        </SectionReveal>

        {done ? (
          <SectionReveal>
            <div className="mt-14 bg-white border border-hairline rounded-2xl p-10 md:p-14 text-center" data-testid="contact-success">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-sage text-cream inline-flex items-center justify-center">
                <Check size={24} strokeWidth={1.3} />
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-ink">Message received.</h2>
              <p className="mt-5 text-ink-soft max-w-lg mx-auto leading-relaxed">
                Pip will respond personally, usually within 2 working days.
              </p>
            </div>
          </SectionReveal>
        ) : (
          <SectionReveal delay={100}>
            <form onSubmit={submit} className="mt-14 bg-white border border-hairline rounded-2xl p-8 md:p-12 space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">First Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-cream-soft border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-cream-soft border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2 text-ink-soft">Your Message</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-cream-soft border border-hairline rounded-2xl px-6 py-4 text-ink focus:outline-none focus:border-sage transition resize-none"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Mail size={16} strokeWidth={1.8} />
                {loading ? "Sending…" : "Send message"}
                {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
              </button>
            </form>
          </SectionReveal>
        )}
      </div>
    </section>
  );
}
